import { 
  VStack, 
  Card, 
  CardHeader, 
  CardBody, 
  Heading, 
  Box, 
  Text,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { TagCloud } from '../TagCloud';
import { MdWarning, MdLightbulb } from 'react-icons/md';

export interface Prediction {
  prediction: string;
  predictor: string;
  tone: string;
}

export interface ActionableInsight {
  opportunity: string;
  reasoning: string;
  supporters: string;
  opponents: string;
}

interface SidebarInsightsProps {
  companies: (string | { name: string; description?: string })[];
  people: (string | { name: string; description?: string })[];
  predictions: Prediction[];
  actionableInsights: ActionableInsight[];
  risks?: string[];
  underTheRadar?: string[];
}

export const SidebarInsights = ({ 
  companies, 
  people, 
  predictions, 
  actionableInsights,
  risks = [],
  underTheRadar = []
}: SidebarInsightsProps) => {
  // Ensure arrays are defined
  const safeCompanies = companies || [];
  const safePeople = people || [];
  const safePredictions = predictions || [];
  const safeInsights = actionableInsights || [];
  const safeRisks = risks || [];
  const safeUnderRadar = underTheRadar || [];
  
  return (
    <VStack spacing={6} align="stretch">
      {/* Companies Mentioned */}
      {safeCompanies.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Companies Mentioned</Heading>
          </CardHeader>
          <CardBody>
            <TagCloud 
              tags={safeCompanies} 
              colorScheme="blue" 
              showTooltips={true}
            />
          </CardBody>
        </Card>
      )}

      {/* People Mentioned */}
      {safePeople.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">People Mentioned</Heading>
          </CardHeader>
          <CardBody>
            <TagCloud 
              tags={safePeople} 
              colorScheme="green" 
              showTooltips={true}
            />
          </CardBody>
        </Card>
      )}

      {/* Actionable Insights */}
      {safeInsights.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Actionable Insights</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {safeInsights.map((insight, index) => (
                <Box key={index} data-testid="actionable-insight-item">
                  <Text fontWeight="bold">{insight.opportunity}</Text>
                  <Text mt={1}>{insight.reasoning}</Text>
                  <Box mt={2} fontSize="sm">
                    <Text color="green.600">Supporters: {insight.supporters}</Text>
                    <Text color="red.600">Opponents: {insight.opponents}</Text>
                  </Box>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Predictions */}
      {safePredictions.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Predictions</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              {safePredictions.map((prediction, index) => (
                <Box key={index} data-testid="prediction-item">
                  <Text>{prediction.prediction}</Text>
                  <Text fontSize="sm" color="gray.500">
                    By: {prediction.predictor} • Tone: {prediction.tone}
                  </Text>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}
      
      {/* Key Risks */}
      {safeRisks.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Key Risks</Heading>
          </CardHeader>
          <CardBody>
            <List spacing={3}>
              {safeRisks.map((risk, index) => (
                <ListItem key={index} display="flex" alignItems="flex-start">
                  <ListIcon as={MdWarning} color="red.500" mt={1} />
                  <Text>{risk}</Text>
                </ListItem>
              ))}
            </List>
          </CardBody>
        </Card>
      )}

      {/* Under the Radar */}
      {safeUnderRadar.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Under the Radar</Heading>
          </CardHeader>
          <CardBody>
            <List spacing={3}>
              {safeUnderRadar.map((item, index) => (
                <ListItem key={index} display="flex" alignItems="flex-start">
                  <ListIcon as={MdLightbulb} color="yellow.500" mt={1} />
                  <Text>{item}</Text>
                </ListItem>
              ))}
            </List>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}; 